import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import {
  AccessGroupScope,
  FreeParkingDashboard,
  FreeParkingDashboardSiteQuota,
  FreeParkingNearFullAlert,
  FreeParkingTopClientByCards,
  mapFreeParkingDashboard,
} from '../../../../core/models/system-settings/access-group.model';

@Component({
  selector: 'app-free-parking-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, PssLoadingComponent],
  templateUrl: './free-parking-dashboard.component.html',
  styleUrl: './free-parking-dashboard.component.css',
})
export class FreeParkingDashboardComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiSystemSettingsService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);

  dashboard = signal<FreeParkingDashboard | null>(null);
  isLoading = signal(false);

  /** Max card count among top clients — for bar width scale */
  topClientMax = computed(() => {
    const rows = this.dashboard()?.topClientsByCards ?? [];
    if (!rows.length) return 1;
    return Math.max(1, ...rows.map((r) => r.cardCount));
  });

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) {
        untracked(() => this.dashboard.set(null));
        return;
      }
      untracked(() => this.loadDashboard());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get scope(): AccessGroupScope | null {
    const s = this.dashboard()?.scope;
    if (!s?.groupId) return null;
    return s;
  }

  get hasGroup(): boolean {
    return !!this.scope?.groupId;
  }

  get totalSiteApproved(): number {
    return (this.dashboard()?.siteQuotas ?? []).reduce((s, r) => s + (r.quotaApproved || 0), 0);
  }

  get totalSiteUsed(): number {
    const fromRows = (this.dashboard()?.siteQuotas ?? []).reduce((s, r) => s + (r.quotaUsed || 0), 0);
    const fromCards = this.dashboard()?.activeFreeCardCount ?? 0;
    // Prefer live free-card count so KPI matches "บัตรจอดฟรีที่ใช้งาน"
    return Math.max(fromRows, fromCards);
  }

  get totalSiteRemainingByCards(): number {
    return Math.max(0, this.totalSiteApproved - this.totalSiteUsed);
  }

  get overallUsedPercent(): number {
    if (this.totalSiteApproved <= 0) return 0;
    return Math.round((1000 * this.totalSiteUsed) / this.totalSiteApproved) / 10;
  }

  ngOnInit(): void {
    if (this.selectedSiteId) {
      this.loadDashboard();
    }
  }

  onRefresh(): void {
    this.loadDashboard();
  }

  siteUsedPercent(row: FreeParkingDashboardSiteQuota): number {
    if (row.usedPercent != null && row.usedPercent >= 0) return row.usedPercent;
    if (!row.quotaApproved) return 0;
    return Math.round((1000 * row.quotaUsed) / row.quotaApproved) / 10;
  }

  barWidthPercent(value: number, max: number): number {
    if (max <= 0) return 0;
    return Math.min(100, Math.round((1000 * value) / max) / 10);
  }

  clientBarWidth(row: FreeParkingTopClientByCards): number {
    return this.barWidthPercent(row.cardCount, this.topClientMax());
  }

  usageBarClass(pct: number): string {
    if (pct >= 90) return 'bg-rose-500';
    if (pct >= 70) return 'bg-amber-400';
    return 'bg-emerald-500';
  }

  alertClass(alert: FreeParkingNearFullAlert): string {
    if (alert.usedPercent >= 95) {
      return 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/30 dark:text-rose-200 dark:border-rose-800';
    }
    return 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/30 dark:text-amber-100 dark:border-amber-800';
  }

  groupTypeLabel(type: string): string {
    return type === 'tenant' ? 'ผู้เช่า' : 'ฝ่ายอาคาร';
  }

  trackSite(_: number, row: FreeParkingDashboardSiteQuota): number {
    return row.id;
  }

  trackTop(_: number, row: FreeParkingTopClientByCards): number {
    return row.clientId;
  }

  private loadDashboard(): void {
    const siteId = this.selectedSiteId;
    const resUserId = this.resolveUserId();
    if (!siteId || !resUserId) return;

    this.isLoading.set(true);
    this.api.getFreeParkingDashboard(resUserId, siteId).subscribe({
      next: (res) => {
        this.dashboard.set(mapFreeParkingDashboard(res));
        this.isLoading.set(false);
      },
      error: () => {
        this.dashboard.set(null);
        this.isLoading.set(false);
        this.toast.error('โหลดแดชบอร์ดล้มเหลว');
      },
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
